import React, { useState } from 'react'
import { XIcon, PlusIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { randomToastSuccess } from '@/shared/Toast'
import PrimaryButton from '@/shared/Buttons/Primary'
import Card from '@/shared/Card'
import SVGIcon from '@/components/icon'
import classNames from '@/utils/classNamesLocal'

// Every platform the profile API accepts. `handle` platforms store a name,
// the rest store a URL. `icon` is a sprite name where one exists.
const PLATFORMS = [
  { key: 'website', label: 'Website', placeholder: 'https://yoursite.com', icon: 'linktree' },
  { key: 'x', label: 'X', handle: true, placeholder: 'CastingCallClub', icon: 'x' },
  { key: 'bluesky', label: 'Bluesky', placeholder: 'https://bsky.app/profile/...', icon: 'bluesky' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...', icon: 'instagram' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@...', icon: 'tiktok' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@...', icon: 'youtube' },
  { key: 'twitch', label: 'Twitch', placeholder: 'https://twitch.tv/...', icon: 'twitch' },
  { key: 'discord', label: 'Discord', handle: true, placeholder: 'yourname', icon: 'discord' },
  { key: 'soundcloud', label: 'SoundCloud', placeholder: 'https://soundcloud.com/...', icon: 'soundcloud' },
  { key: 'spotify', label: 'Spotify', placeholder: 'https://open.spotify.com/...', icon: 'spotify' },
  { key: 'imdb', label: 'IMDb', placeholder: 'https://imdb.com/name/...', icon: 'imdb' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/...', icon: 'linkedin' },
  { key: 'artstation', label: 'ArtStation', placeholder: 'https://artstation.com/...', icon: 'artstation' },
  { key: 'deviantart', label: 'DeviantArt', placeholder: 'https://deviantart.com/...', icon: 'deviantart' },
  { key: 'github', label: 'GitHub', placeholder: 'https://github.com/...', icon: 'github' },
  { key: 'tumblr', label: 'Tumblr', placeholder: 'https://....tumblr.com', icon: 'tumblr' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...', icon: 'facebook' },
  { key: 'kofi', label: 'Tips and subscriptions', placeholder: 'https://ko-fi.com/... or Patreon', icon: 'kofi', hint: 'Patreon, Ko-fi, Buy Me a Coffee, that kind of thing.' }
]

const PlatformIcon = ({ p }) => <SVGIcon name={p.icon} className='h-5 w-5' />

// Links show only when filled. Everything else hides behind "Add a link".
const ProfileLinks = () => {
  const [user, setUser] = useGlobalState('userContainerUser')
  const [, setToast] = useGlobalState('toast')
  const { putpostRequest } = useQuery()
  const saved = user.socialLinks || {}
  const [links, setLinks] = useState(() => Object.fromEntries(PLATFORMS.map((p) => [p.key, saved[p.key] || ''])))
  const [shown, setShown] = useState(() => PLATFORMS.filter((p) => saved[p.key]).map((p) => p.key))
  const [picking, setPicking] = useState(false)
  const [loading, setLoading] = useState(false)

  const dirty = PLATFORMS.some((p) => (links[p.key] || '') !== (saved[p.key] || ''))
  const hidden = PLATFORMS.filter((p) => !shown.includes(p.key))

  const add = (key) => { setShown([...shown, key]); setPicking(false) }
  const remove = (key) => { setShown(shown.filter((k) => k !== key)); setLinks({ ...links, [key]: '' }) }

  const save = () => {
    setLoading(true)
    putpostRequest(`/api/v3/profiles/${user.profileId}`, 'PATCH', { profile: { social_links: links } }, (err) => {
      setLoading(false)
      if (err) { return }
      setUser({ ...user, socialLinks: { ...links } })
      setToast(randomToastSuccess())
    })
  }

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Links</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Where else people can find you. These are public.</p>
    </div>
    <Card footer={<div className='flex justify-end'><PrimaryButton onClick={save} loading={loading} disabled={!dirty} isDirty={dirty} text='Save' /></div>}>
      <div className='space-y-3'>
        { shown.length === 0 && <p className='text-sm text-gray-500 dark:text-gray-400'>No links yet. Add your website, socials, or a place people can tip you.</p> }
        { shown.map((key) => {
          const p = PLATFORMS.find((x) => x.key === key)
          return <div key={key} className='flex items-center gap-x-2'>
            <span className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200'><PlatformIcon p={p} /></span>
            <div className='min-w-0 flex-1'>
              <div className='flex rounded-md shadow-sm'>
                { p.handle && <span className='inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500 dark:bg-gray-900'>@</span> }
                <input type='text' value={links[key]} onChange={(e) => setLinks({ ...links, [key]: e.target.value })} placeholder={p.placeholder} aria-label={p.label}
                  className={classNames(p.handle && '!rounded-l-none')} autoFocus={!saved[key]} />
              </div>
              { p.hint && <p className='mt-0.5 text-xs text-gray-500 dark:text-gray-400'>{p.hint}</p> }
            </div>
            <span className='hidden w-28 truncate text-xs text-gray-500 dark:text-gray-400 sm:block'>{p.label}</span>
            <button type='button' onClick={() => remove(key)} aria-label={`Remove ${p.label}`} className='rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700'><XIcon className='h-4 w-4' /></button>
          </div>
        })}

        { hidden.length > 0 && <div className='relative'>
          <button type='button' onClick={() => setPicking(!picking)} aria-expanded={picking}
            className='inline-flex items-center gap-x-1 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-800 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-cccblue dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'>
            <PlusIcon className='h-4 w-4' /> Add a link
          </button>
          { picking && <div className='mt-2 flex flex-wrap gap-2 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-800'>
            { hidden.map((p) => (
              <button key={p.key} type='button' onClick={() => add(p.key)}
                className='inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:ring-cccblue hover:text-cccblue dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600'>
                <PlatformIcon p={p} /> {p.label}
              </button>
            ))}
          </div> }
        </div> }
      </div>
    </Card>
  </div>
}

export default ProfileLinks
